import { useEffect, useState } from "react";
import { api, apiError } from "../lib/api";
import { PageHeader } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { ShieldCheck, Lock } from "@phosphor-icons/react";
import { toast } from "sonner";

const ROLE_LABELS = { admin: "Admin", ops: "Operations", finance: "Finance" };

export default function Permissions() {
  const [modules, setModules] = useState([]);
  const [editableRoles, setEditableRoles] = useState([]);
  const [matrix, setMatrix] = useState({});
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/permissions").then((r) => {
    setModules(r.data.modules);
    setEditableRoles(r.data.editable_roles);
    setMatrix(r.data.matrix);
  }).catch((e) => toast.error(apiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const roles = ["admin", ...editableRoles];
  const toggle = (role, key) => {
    setMatrix((m) => {
      const have = m[role]?.includes(key);
      return { ...m, [role]: have ? m[role].filter((k) => k !== key) : [...(m[role] || []), key] };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { matrix: {} };
      editableRoles.forEach((r) => { payload.matrix[r] = matrix[r] || []; });
      await api.put("/permissions", payload);
      toast.success("Access settings saved");
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <>
      <PageHeader title="Roles & Access" subtitle="Control which CRM modules each role can access"
        actions={<Button className="rounded-sm" onClick={save} disabled={busy} data-testid="permissions-save">{busy ? "Saving…" : "Save Changes"}</Button>} />
      <div className="p-8">
        <Card className="rounded-sm border-border overflow-hidden">
          <div className="px-6 py-4 border-b border-border flex items-center gap-2">
            <ShieldCheck size={18} weight="bold" className="text-primary" />
            <h3 className="font-heading font-semibold">Module Permissions</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="permissions-matrix">
              <thead>
                <tr className="bg-secondary/50 text-left">
                  <th className="px-6 py-3 font-semibold">CRM Module</th>
                  {roles.map((r) => (
                    <th key={r} className="px-6 py-3 font-semibold text-center">
                      {ROLE_LABELS[r] || r}
                      {r === "admin" && <span className="block text-[10px] font-normal text-muted-foreground"><Lock size={10} className="inline" /> full access</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {modules.map((mod) => (
                  <tr key={mod.key} className="border-t border-border hover:bg-secondary/30">
                    <td className="px-6 py-3 font-medium">{mod.label}</td>
                    {roles.map((r) => {
                      const checked = r === "admin" ? true : (matrix[r]?.includes(mod.key) || false);
                      return (
                        <td key={r} className="px-6 py-3 text-center">
                          <Checkbox checked={checked} disabled={r === "admin"}
                            onCheckedChange={() => toggle(r, mod.key)}
                            data-testid={`perm-${r}-${mod.key}`} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
        <p className="text-xs text-muted-foreground mt-3">Admin always has full access and cannot be restricted. Changes apply immediately to Operations and Finance users on their next request.</p>
      </div>
    </>
  );
}
